import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { Store } from 'lucide-react';
import { db } from '@/lib/firebase';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCurrentStore } from '@/hooks/useCurrentStore';
import type { Store as StoreType } from '@/types/schemas';

export function StoreSwitcher() {
  const { currentStoreId, setCurrentStoreId } = useCurrentStore();

  const { data: stores } = useQuery({
    queryKey: ['stores'],
    queryFn: async (): Promise<StoreType[]> => {
      const snap = await getDocs(query(collection(db, 'stores'), orderBy('name')));
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<StoreType, 'id'>) }));
    },
  });

  useEffect(() => {
    if (stores && stores.length && !currentStoreId) {
      setCurrentStoreId(stores[0].id);
    }
  }, [stores, currentStoreId, setCurrentStoreId]);

  if (!stores || stores.length === 0) {
    return (
      <span className="hidden text-xs text-muted-foreground sm:inline">No stores yet</span>
    );
  }

  const currentStore = stores.find((s) => s.id === currentStoreId);

  return (
    <Select
      value={currentStoreId ?? undefined}
      onValueChange={(v) => setCurrentStoreId(v)}
    >
      {/* On very small screens show just the store icon; on sm+ show the full trigger */}
      <SelectTrigger className="h-8 w-8 border-0 bg-transparent p-0 shadow-none focus:ring-0 min-[380px]:w-[130px] min-[380px]:border min-[380px]:bg-background min-[380px]:px-3 min-[380px]:shadow-sm sm:w-[180px]">
        {/* Icon-only on tiny screens */}
        <span className="min-[380px]:hidden">
          <Store className="h-4 w-4 text-muted-foreground" />
        </span>
        {/* Full select value on larger screens */}
        <span className="hidden min-[380px]:contents">
          <SelectValue placeholder="Select store">
            {currentStore?.name ?? 'Select store'}
          </SelectValue>
        </span>
      </SelectTrigger>
      <SelectContent>
        {stores.map((s) => (
          <SelectItem key={s.id} value={s.id}>
            {s.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
